import React, { FunctionComponent, ReactNode, useState } from 'react';
import { StateValues } from 'react-use-form-state';
import { AdminProductDistributionPlatformListing, CapitalTypeEnum, Document } from 'api';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons, makeModalForm } from 'ui/molecules/modal';
import Spacer from 'src/ui/atoms/spacer';
import * as Styled from './styled';
import Tooltip, { TooltipPosition } from 'src/ui/atoms/tooltip';
import useIsMedia, { DEVICES } from 'ui/hooks/use-is-media';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import DatePicker from 'src/ui/atoms/date-picker';
import ServerError from 'src/ui/types/server-error';
import { ExtendedDistributionInformation } from '../list-distribution-informations';
import moment from 'moment';
import Hint from 'src/ui/atoms/hint';
import { FileWithDocType } from 'src/ui/atoms/file-upload';
import FileUploadDocumentContainer from 'src/ui/atoms/file-upload/file-upload-document-container';
import Section from 'src/ui/atoms/section';
import Header from 'src/ui/atoms/header';
import Icon from 'src/ui/atoms/icon';

interface DistributionInformationModalProps {
  platformsWithNoDistributionInformation: AdminProductDistributionPlatformListing[];
  onSubmit: (
    fieldsValues: StateValues<DistributionInformationModalFields>,
    productStartDate?: Date | null,
    productEndDate?: Date | null,
    uploadedImage?: Document | null,
  ) => void;
  onClose: () => void;
  modalHeader: ReactNode;
  loading?: boolean;
  error?: ServerError;
  data?: ExtendedDistributionInformation;
}

export interface DistributionInformationModalFields {
  distributionPlatform: string;
  complianceProductName: string;
  useUnits: string;
  unitSteps: string;
  selfDisclosureRequired: boolean;
  investmentExperienceMinScore: number;
  serviceExperienceMinScore: number;
  capitalType: CapitalTypeEnum;
  runtimeDate: string;
  productDate: Date;
  runtimeYears?: string;
  costsBrokerageNominalAmountPercent: number;
  grantsNominalAmountPercent: number;
  costsBrokerageNavPercent: number;
  grantsNavPercent: number;
  costsTransactionDepotNominalAmountPercent: number;
  costsTransactionDepotNavPercent: number;
  productImage: FileWithDocType;
}

export const Form = makeModalForm<DistributionInformationModalFields>();

const DistributionInformationModal: FunctionComponent<DistributionInformationModalProps> = ({
  platformsWithNoDistributionInformation,
  modalHeader,
  loading,
  error,
  onSubmit,
  onClose,
  data,
}) => {
  const [isConfirmEditView, setConfirmEditView] = useState(false);
  const [isRuntimeDate, setRuntimeDate] = useState(data ? !!data.costsvariables?.productStartDate : true);
  const [uploadedImage, setUploadedImage] = useState<Document | undefined | null>(data?.productImage || null);
  const [productStartDate, setProductStartDate] = useState(data?.costsvariables?.productStartDate);
  const [productEndDate, setProductEndDate] = useState(data?.costsvariables?.productEndDate);

  const isPhone = useIsMedia(DEVICES.phone);
  const translate = useTranslateWithStringArgs();

  const availablePlatforms = React.useMemo(() => {
    if (data) {
      return [{ value: data.platformId, label: data.platformName }];
    }
    return platformsWithNoDistributionInformation.map((platform) => ({
      value: platform.id,
      label: platform.distributionPlatformDisplayName,
    }));
  }, [JSON.stringify(data), JSON.stringify(platformsWithNoDistributionInformation)]);

  if (availablePlatforms.length === 0) return null;

  return (
    <Modal onClose={onClose} size="large">
      <Form
        i18nKey="distributionInformation"
        onSubmit={(values: StateValues<DistributionInformationModalFields>) => {
          if (data && !isConfirmEditView) {
            setConfirmEditView(true);
          } else {
            onSubmit(values, productStartDate, productEndDate, uploadedImage);
          }
        }}
        onChange={(values: StateValues<DistributionInformationModalFields>) => {
          setRuntimeDate(values.runtimeDate === 'true');
        }}
        error={error}
        initial={{
          ...(data && {
            complianceProductName: data.complianceProductName,
            useUnits: data.useUnits.toString(),
            unitSteps: data.unitSteps,
            selfDisclosureRequired: data.selfDisclosureRequired,
            investmentExperienceMinScore: data.investmentExperienceMinScore!,
            serviceExperienceMinScore: data.serviceExperienceMinScore!,
            capitalType: data.costsvariables?.capitalType,
            runtimeDate:
              data.costsvariables?.runtimeYears && data.costsvariables?.runtimeYears !== 0 ? 'false' : 'true',
            runtimeYears: data.costsvariables?.runtimeYears ? data.costsvariables?.runtimeYears.toString() : undefined,
            costsBrokerageNominalAmountPercent: Number(data.costsvariables?.costsBrokerageNominalAmountPercent!),
            grantsNominalAmountPercent: Number(data.costsvariables?.grantsNominalAmountPercent!),
            costsBrokerageNavPercent: Number(data.costsvariables?.costsBrokerageNavPercent!),
            grantsNavPercent: Number(data.costsvariables?.grantsNavPercent!),
            costsTransactionDepotNominalAmountPercent: Number(
              data.costsvariables?.costsTransactionDepotNominalAmountPercent!,
            ),
            costsTransactionDepotNavPercent: Number(data.costsvariables?.costsTransactionDepotNavPercent!),
          }),
          distributionPlatform: availablePlatforms.length === 1 ? availablePlatforms[0]?.value : '',
        }}
      >
        <ModalHeader>{modalHeader}</ModalHeader>
        <ModalContent>
          {isConfirmEditView && (
            <>
              <p>
                <Translate name="distributionInformation.editQuestion" />
              </p>
              <Hint variant="warning">
                <Translate name="distributionInformation.editWarning" />
              </Hint>
            </>
          )}
          {!isConfirmEditView && (
            <>
              <Section spacing="regular">
                <Header spacing="medium">
                  <Translate name="distributionInformation.general" />
                </Header>
                <Form.Group name="distributionPlatform" required={true} occupyTooltipSpace={true}>
                  <Form.Select options={availablePlatforms} disabled={availablePlatforms.length === 1}></Form.Select>
                </Form.Group>
                <Form.Group
                  name="complianceProductName"
                  required={true}
                  tooltip={
                    <Tooltip
                      content={<Translate name="distributionInformation.fields.complianceProductName.tooltip" />}
                      position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                    >
                      <Icon name="question" size="large" color="info" padded="none" />
                    </Tooltip>
                  }
                >
                  <Form.Input />
                </Form.Group>

                <Section spacing="small">
                  <Styled.RadioContainer>
                    <Styled.LabelRadio size="tiny">
                      <Translate name="distributionInformation.fields.useUnits.label" />
                    </Styled.LabelRadio>
                    <Form.Group
                      name="useUnits"
                      spacing="none"
                      required={true}
                      forceLabelOnNewLine={true}
                      label={null}
                      tooltip={
                        <Tooltip
                          content={<Translate name="distributionInformation.fields.useUnits.tooltip" />}
                          position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                        >
                          <Icon name="question" size="large" color="info" padded="none" />
                        </Tooltip>
                      }
                    >
                      <Form.GroupToggle>
                        <Form.Radio value="true" key="true">
                          <Translate name="distributionInformation.fields.useUnits.units" />
                        </Form.Radio>
                        <Form.Radio value="false" key="false">
                          <Translate name="distributionInformation.fields.useUnits.amount" />
                        </Form.Radio>
                      </Form.GroupToggle>
                    </Form.Group>
                  </Styled.RadioContainer>
                </Section>
                <Form.Group
                  name="unitSteps"
                  tooltip={
                    <Tooltip
                      content={<Translate name="distributionInformation.fields.unitSteps.tooltip" />}
                      position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                    >
                      <Icon name="question" size="large" color="info" padded="none" />
                    </Tooltip>
                  }
                >
                  <Form.Input />
                </Form.Group>
                <Form.Group
                  name="selfDisclosureRequired"
                  label={null}
                  tooltip={
                    <Tooltip
                      content={<Translate name="distributionInformation.fields.selfDisclosureRequired.tooltip" />}
                      position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                    >
                      <Icon name="question" size="large" color="info" padded="none" />
                    </Tooltip>
                  }
                >
                  <Form.Checkbox>
                    <Translate name="distributionInformation.fields.selfDisclosureRequired.label" />
                  </Form.Checkbox>
                </Form.Group>
              </Section>

              <Section spacing="regular">
                <Header spacing="medium">
                  <Translate name="distributionInformation.targetMarket" />
                </Header>
                <Styled.InputSubtitle size="tiny">
                  <Translate name="distributionInformation.fields.investmentExperienceMinScore.subtitle" />
                </Styled.InputSubtitle>
                <Form.Group
                  name="investmentExperienceMinScore"
                  required={true}
                  tooltip={
                    <Tooltip
                      content={<Translate name="distributionInformation.fields.investmentExperienceMinScore.tooltip" />}
                      position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                    >
                      <Icon name="question" size="large" color="info" padded="none" />
                    </Tooltip>
                  }
                >
                  <Form.Input />
                </Form.Group>
                <Styled.InputSubtitle size="tiny">
                  <Translate name="distributionInformation.fields.serviceExperienceMinScore.subtitle" />
                </Styled.InputSubtitle>
                <Form.Group
                  name="serviceExperienceMinScore"
                  required={true}
                  tooltip={
                    <Tooltip
                      content={<Translate name="distributionInformation.fields.serviceExperienceMinScore.tooltip" />}
                      position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                    >
                      <Icon name="question" size="large" color="info" padded="none" />
                    </Tooltip>
                  }
                >
                  <Form.Input />
                </Form.Group>
              </Section>

              <Section spacing="regular">
                <Header spacing="medium">
                  <Translate name="distributionInformation.costs" />
                </Header>
                <Section spacing="small">
                  <Styled.RadioContainer>
                    <Styled.LabelRadio size="tiny">
                      <Translate name="distributionInformation.fields.capitalType.label" />
                    </Styled.LabelRadio>
                    <Form.Group
                      name="capitalType"
                      spacing="none"
                      required={true}
                      forceLabelOnNewLine={true}
                      label={null}
                      tooltip={
                        <Tooltip
                          content={<Translate name="distributionInformation.fields.capitalType.tooltip" />}
                          position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                        >
                          <Icon name="question" size="large" color="info" padded="none" />
                        </Tooltip>
                      }
                    >
                      <Form.GroupToggle>
                        <Form.Radio value={CapitalTypeEnum.DEBT} key={CapitalTypeEnum.DEBT}>
                          <Translate name="distributionInformation.fields.capitalType.debt" />
                        </Form.Radio>
                        <Form.Radio value={CapitalTypeEnum.EQUITY} key={CapitalTypeEnum.EQUITY}>
                          <Translate name="distributionInformation.fields.capitalType.equity" />
                        </Form.Radio>
                      </Form.GroupToggle>
                    </Form.Group>
                  </Styled.RadioContainer>
                </Section>
                <Section spacing="small">
                  <Styled.RadioContainer>
                    <Styled.LabelRadio size="tiny">
                      <Translate name="distributionInformation.fields.runtime.label" />
                    </Styled.LabelRadio>
                    <Form.Group
                      name="runtimeDate"
                      spacing="none"
                      required={true}
                      forceLabelOnNewLine={true}
                      label={null}
                      tooltip={
                        <Tooltip
                          content={<Translate name="distributionInformation.fields.runtime.tooltip" />}
                          position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                        >
                          <Icon name="question" size="large" color="info" padded="none" />
                        </Tooltip>
                      }
                    >
                      <Form.GroupToggle>
                        <Form.Radio value="true" key="true">
                          <Translate name="distributionInformation.fields.productDate.label" />
                        </Form.Radio>
                        <Form.Radio value="false" key="false">
                          <Translate name="distributionInformation.fields.runtimeYears.label" />
                        </Form.Radio>
                      </Form.GroupToggle>
                    </Form.Group>
                  </Styled.RadioContainer>
                </Section>
                <Spacer y={2} />
                {isRuntimeDate && (
                  <Form.Group
                    name="productDate"
                    label={null}
                    tooltip={
                      <Tooltip
                        content={<Translate name="distributionInformation.fields.productDate.tooltip" />}
                        position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                      >
                        <Icon name="question" size="large" color="info" padded="none" />
                      </Tooltip>
                    }
                  >
                    <Styled.RuntimeDateContainer>
                      <DatePicker
                        placeholder={translate('distributionInformation.fields.productDate.start')}
                        value={productStartDate ? moment(productStartDate).format('DD.MM.YYYY') : undefined}
                        onChange={(value: any) => {
                          setProductStartDate(value);
                        }}
                      />
                      <Spacer x={4} />
                      <DatePicker
                        placeholder={translate('distributionInformation.fields.productDate.end')}
                        value={productEndDate ? moment(productEndDate).format('DD.MM.YYYY') : undefined}
                        minDate={new Date()}
                        onChange={(value: any) => {
                          setProductEndDate(value);
                        }}
                      />
                    </Styled.RuntimeDateContainer>
                  </Form.Group>
                )}
                {!isRuntimeDate && (
                  <Form.Group
                    name="runtimeYears"
                    required={!isRuntimeDate}
                    tooltip={
                      <Tooltip
                        content={<Translate name="distributionInformation.fields.runtimeYears.tooltip" />}
                        position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                      >
                        <Icon name="question" size="large" color="info" padded="none" />
                      </Tooltip>
                    }
                  >
                    <Form.Input type="number" />
                  </Form.Group>
                )}
                <Styled.InputSubtitle size="tiny">
                  <Translate name="distributionInformation.fields.costsBrokerageNominalAmountPercent.subtitle" />
                </Styled.InputSubtitle>
                <Form.Group
                  name="costsBrokerageNominalAmountPercent"
                  required={true}
                  tooltip={
                    <Tooltip
                      content={<Translate name="distributionInformation.fields.costsTooltip" />}
                      position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                    >
                      <Icon name="question" size="large" color="info" padded="none" />
                    </Tooltip>
                  }
                >
                  <Form.Input type="number" min={0} max={100} isPercentage />
                </Form.Group>
                <Styled.InputSubtitle size="tiny">
                  <Translate name="distributionInformation.fields.grantsNominalAmountPercent.subtitle" />
                </Styled.InputSubtitle>
                <Form.Group
                  name="grantsNominalAmountPercent"
                  required={true}
                  tooltip={
                    <Tooltip
                      content={<Translate name="distributionInformation.fields.costsTooltip" />}
                      position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                    >
                      <Icon name="question" size="large" color="info" padded="none" />
                    </Tooltip>
                  }
                >
                  <Form.Input type="number" min={0} max={100} isPercentage />
                </Form.Group>
                <Styled.InputSubtitle size="tiny">
                  <Translate name="distributionInformation.fields.costsBrokerageNavPercent.subtitle" />
                </Styled.InputSubtitle>
                <Form.Group
                  name="costsBrokerageNavPercent"
                  required={true}
                  tooltip={
                    <Tooltip
                      content={<Translate name="distributionInformation.fields.costsTooltip" />}
                      position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                    >
                      <Icon name="question" size="large" color="info" padded="none" />
                    </Tooltip>
                  }
                >
                  <Form.Input type="number" min={0} max={100} isPercentage />
                </Form.Group>
                <Styled.InputSubtitle size="tiny">
                  <Translate name="distributionInformation.fields.grantsNavPercent.subtitle" />
                </Styled.InputSubtitle>
                <Form.Group
                  name="grantsNavPercent"
                  required={true}
                  tooltip={
                    <Tooltip
                      content={<Translate name="distributionInformation.fields.costsTooltip" />}
                      position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                    >
                      <Icon name="question" size="large" color="info" padded="none" />
                    </Tooltip>
                  }
                >
                  <Form.Input type="number" min={0} max={100} isPercentage />
                </Form.Group>
                <Styled.InputSubtitle size="tiny">
                  <Translate name="distributionInformation.fields.costsTransactionDepotNominalAmountPercent.subtitle" />
                </Styled.InputSubtitle>
                <Form.Group
                  name="costsTransactionDepotNominalAmountPercent"
                  required={true}
                  tooltip={
                    <Tooltip
                      content={<Translate name="distributionInformation.fields.costsTooltip" />}
                      position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                    >
                      <Icon name="question" size="large" color="info" />
                    </Tooltip>
                  }
                >
                  <Form.Input type="number" min={0} max={100} isPercentage />
                </Form.Group>
                <Styled.InputSubtitle size="tiny">
                  <Translate name="distributionInformation.fields.costsTransactionDepotNavPercent.subtitle" />
                </Styled.InputSubtitle>
                <Form.Group
                  name="costsTransactionDepotNavPercent"
                  required={true}
                  tooltip={
                    <Tooltip
                      content={<Translate name="distributionInformation.fields.costsTooltip" />}
                      position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                    >
                      <Icon name="question" size="large" color="info" padded="none" />
                    </Tooltip>
                  }
                >
                  <Form.Input type="number" min={0} max={100} isPercentage />
                </Form.Group>
              </Section>

              {uploadedImage && (
                <FileUploadDocumentContainer
                  uploadedFile={{
                    file: {
                      name: uploadedImage.name,
                      size: uploadedImage.sizeInBytes,
                    } as File,
                  }}
                  onRemoveDocument={() => setUploadedImage(null)}
                />
              )}
              {!uploadedImage && (
                <Form.Group name="productImage">
                  <Form.FileUpload fileType="image" />
                </Form.Group>
              )}
            </>
          )}
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons
            actionButtons={[
              {
                name: 'cancel',
                content: <Translate name="common.cancel" />,
                size: 'large',
                onClick: onClose,
              },
              {
                name: 'save',
                content: data ? <Translate name="common.saveChanges" /> : <Translate name="common.add" />,
                variant: 'primary',
                type: 'submit',
                size: 'large',
                loading: loading,
              },
            ]}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default DistributionInformationModal;
